import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ArrowRight } from "lucide-react";
import { usePortfolio } from "@/lib/PortfolioProvider";
import { projectSlug, findProjectBySlug } from "@/lib/slug";
import { trackEvent, trackOutbound } from "@/analytics/ga4";
import PageTransition from "@/components/site/PageTransition";
import SectionGlow from "@/components/site/SectionGlow";
import Reveal from "@/components/site/Reveal";

const MetaRow = ({ label, value }) => (
    <div
        className="flex items-center justify-between gap-4 border-b pb-4 last:border-0 last:pb-0"
        style={{ borderColor: "var(--pg-border)" }}
    >
        <dt className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--pg-fg-muted)" }}>{label}</dt>
        <dd className="font-display text-base" style={{ color: "var(--pg-fg)" }}>{value}</dd>
    </div>
);

/**
 * ProjectDetail — reusable, editorial /work/:slug foundation built entirely on
 * the existing CMS portfolio data (title, category, summary, hero image,
 * external link). Gallery + client + year render only when the data provides
 * them, so deeper case-study blocks can be layered on later without a rewrite.
 */
export default function ProjectDetail() {
    const { slug } = useParams();
    const { branding } = usePortfolio();
    const project = useMemo(() => findProjectBySlug(branding, slug), [branding, slug]);
    const reduce = useReducedMotion();

    useEffect(() => {
        if (project) trackEvent("project_view", { category: project.category, item_id: project.id });
    }, [project]);

    if (!project) {
        return (
            <PageTransition>
                <section className="pg-section pt-32 sm:pt-36" data-testid="project-not-found">
                    <div className="pg-container text-center">
                        <span className="pg-eyebrow justify-center">Project not found</span>
                        <h1 className="font-display mt-5 text-[clamp(2rem,5vw,3.4rem)]" style={{ color: "var(--pg-fg)" }}>
                            This project isn&rsquo;t available.
                        </h1>
                        <Link to="/work" className="pg-btn-primary mt-8 inline-flex" data-testid="project-back-all">
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.6} /> Back to all work
                        </Link>
                    </div>
                </section>
            </PageTransition>
        );
    }

    const gallery = Array.isArray(project.images) && project.images.length ? project.images : [];
    const idx = branding.findIndex((p) => projectSlug(p) === projectSlug(project));
    const next = branding.length > 1 ? branding[(idx + 1 + branding.length) % branding.length] : null;

    return (
        <PageTransition>
            {/* Full-bleed cinematic hero — a single exhibition "moment" that
                locks to a deep ink treatment (per the approved cinematic-lock
                allowance for project media) regardless of the site theme. */}
            <section
                className="relative w-full overflow-hidden"
                data-testid="project-detail"
                data-project-id={project.id}
                aria-label="Project hero"
            >
                <div className="relative h-[82svh] min-h-[540px] w-full">
                    <motion.img
                        src={project.image}
                        alt={`${project.title}, ${project.category}`}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="eager"
                        initial={reduce ? { scale: 1 } : { scale: 1.09 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: reduce ? 0 : 1.8, ease: [0.16, 1, 0.3, 1] }}
                        draggable={false}
                    />
                    {/* Cinematic scrims — top for the nav, bottom for the title */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(180deg, rgba(4,16,15,0.62) 0%, rgba(4,16,15,0.06) 30%, rgba(4,16,15,0.30) 60%, rgba(4,16,15,0.94) 100%)",
                        }}
                    />
                    <div
                        className="absolute inset-0"
                        style={{ background: "linear-gradient(90deg, rgba(4,16,15,0.78) 0%, rgba(4,16,15,0.18) 46%, transparent 68%)" }}
                    />
                    <div className="pg-noise" />

                    {/* Overlaid title content, anchored bottom-left */}
                    <div className="absolute inset-x-0 bottom-0">
                        <div className="pg-container pb-10 sm:pb-14 lg:pb-16">
                            <div className="flex flex-wrap items-end justify-between gap-8">
                                <motion.div
                                    className="max-w-3xl"
                                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: 26 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: reduce ? 0.4 : 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <nav aria-label="Breadcrumb" className="mb-5 flex flex-wrap items-center gap-1.5">
                                        <Link to="/" className="pg-link-underline font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">Home</Link>
                                        <span className="text-white/40">/</span>
                                        <Link to="/work" className="pg-link-underline font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">Work</Link>
                                        <span className="text-white/40">/</span>
                                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white">{project.title}</span>
                                    </nav>
                                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--pg-accent-soft,#FFDCA6)]">
                                        {project.category}
                                    </span>
                                    <h1 className="pg-h1-page mt-4 text-white" data-testid="project-title">
                                        {project.title}
                                    </h1>
                                    {project.summary && (
                                        <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
                                            {project.summary}
                                        </p>
                                    )}
                                </motion.div>

                                <motion.div
                                    className="hidden shrink-0 flex-col items-end gap-3 sm:flex"
                                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: reduce ? 0.4 : 0.9, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    {project.year && (
                                        <div className="text-right">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">Year</div>
                                            <div className="font-display text-lg text-white">{project.year}</div>
                                        </div>
                                    )}
                                    {project.client && (
                                        <div className="text-right">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">Client</div>
                                            <div className="font-display text-lg text-white">{project.client}</div>
                                        </div>
                                    )}
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Overview + metadata */}
            <section className="pg-section" aria-label="Project overview">
                <SectionGlow tone="warm" />
                <div className="pg-container">
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
                        <Reveal className="lg:col-span-7">
                            <p className="text-lg leading-relaxed sm:text-xl" style={{ color: "var(--pg-fg)" }}>{project.description}</p>
                            {project.behance && (
                                <a
                                    href={project.behance}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackOutbound("behance")}
                                    data-testid="project-behance-link"
                                    className="pg-btn-primary mt-8 inline-flex"
                                >
                                    View full case study on Behance
                                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
                                </a>
                            )}
                        </Reveal>
                        <Reveal delay={0.08} className="lg:col-span-5">
                            <div className="rounded-3xl border p-6 sm:p-7" style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}>
                                <dl className="space-y-5">
                                    <MetaRow label="Discipline" value={project.category} />
                                    {project.client && <MetaRow label="Client" value={project.client} />}
                                    <MetaRow label="Studio" value="Pranto.Gfx" />
                                    {project.year && <MetaRow label="Year" value={project.year} />}
                                </dl>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Optional editorial gallery (renders only when CMS provides images) */}
            {gallery.length > 0 && (
                <section className="pg-section" aria-label="Project gallery" style={{ paddingTop: 0 }}>
                    <div className="pg-container">
                        <div className="grid grid-cols-1 items-start gap-4 sm:gap-5 lg:grid-cols-12">
                            {gallery.map((src, i) => (
                                <Reveal key={i} delay={0.04 * (i % 6)} className={i % 3 === 0 ? "lg:col-span-12" : "lg:col-span-6"}>
                                    <div
                                        className={`relative overflow-hidden rounded-3xl border ${i % 3 === 0 ? "aspect-[16/9]" : "aspect-[4/3]"}`}
                                        style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                                    >
                                        <img src={src} alt={`${project.title} detail ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Next project */}
            {next && (
                <section className="pg-section" aria-label="Next project" style={{ paddingTop: 0 }}>
                    <div className="pg-container">
                        <Link
                            to={`/work/${projectSlug(next)}`}
                            data-testid="project-next"
                            className="group relative block overflow-hidden rounded-[1.5rem] border"
                            style={{ borderColor: "var(--pg-border)" }}
                        >
                            <div className="relative aspect-[16/8] overflow-hidden">
                                <img
                                    src={next.image}
                                    alt={next.title}
                                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,17,18,0.15) 0%, rgba(7,17,18,0.8) 100%)" }} />
                                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 sm:p-8">
                                    <div>
                                        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/80">Next Project</span>
                                        <div className="mt-2 font-display text-2xl text-white sm:text-4xl">{next.title}</div>
                                    </div>
                                    <span
                                        className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border text-white transition-colors duration-300 group-hover:border-[var(--pg-accent)]"
                                        style={{ borderColor: "rgba(255,255,255,0.4)" }}
                                    >
                                        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.6} />
                                    </span>
                                </div>
                            </div>
                        </Link>
                    </div>
                </section>
            )}
        </PageTransition>
    );
}
